<template>
  <div v-if="!isShipmentReadonly || nonDeleteable">
    <alert ref="msg" />

    <v-badge
      :id="'badge_' + flag"
      :value="!!attachmentCount"
      overlap
      color="primary"
    >
      <template v-slot:badge>
        <span>{{ attachmentCount }}</span>
      </template>
      <v-btn icon
        @click.stop="returnFlag === undefined || returnFlag === true ? onMenuBtn() : null"
        :color="!attachmentCounted ? 'grey--text text--lighten-1' : 'text--secondary'"
        :loading="loading"
      >
        <v-icon>attach_email</v-icon>
      </v-btn>
    </v-badge>

    <v-menu
      :activator="'#badge_' + flag"
      anchor="bottom end"
      v-model="isMenu"
      min-width="480px"
      offset-y
      :close-on-content-click="false"
    >
      <v-card><v-card-text class="pa-2">

        <v-list class="py-0">
          <template v-for="attch in attachments">
            <v-list-tile :key="attch.id" @click="downloadAttachment(attch.id)" :disabled="isShipmentReadonly">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-list-tile-avatar slot="activator" :color="getAttachmentColor(attch.type) + ' white--text'">
                    {{ $t('ATTACHMENT.TYPE.' + (attch.type ? attch.type.toUpperCase() : 'NUL')) }}
                  </v-list-tile-avatar>
                  <v-icon small color="white">get_app</v-icon>{{ $t('Download') }}
                  {{ $t('ATTACHMENT.TYPE.' + (attch.type ? attch.type.toUpperCase() : 'NUL') + '.TEXT') }}
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title v-if="attch.name" class="font-weight-bold" :class="getAttachmentColor(attch.type) + '--text'">{{ attch.name }}</v-list-tile-title>
                <v-list-tile-title v-else            class="font-italic grey--text">{{ $t('untitled') }}</v-list-tile-title>
                <v-list-tile-sub-title>
                  <span class="text--primary">{{ attch.fileName }}</span>
                  <span class="float-right">
                    {{ attch.fileSize }} B
                    <span class="mx-2">&mdash;</span>
                    {{ formatDatetime(attch.fileDate) }}
                  </span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action v-if="!nonDeleteable">
                <v-btn flat icon @click.stop="deleteAttachment(attch.id)" :loading="loading">
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-list-tile-action>
            </v-list-tile>
            <v-divider></v-divider>
          </template>
        </v-list>

        <v-layout row wrap align-start :class="attachments.length ? 'mt-4' : ''">
          <v-flex xs12>
            <v-tooltip bottom>
              <v-input-file slot="activator" @change="uploadAttachment" block :label="$t('Browse for file to upload')"></v-input-file>
              <span>{{ $t('File upload') }}</span>
            </v-tooltip>
          </v-flex>
          <v-flex xs12 sm8>
            <v-tooltip bottom>
              <v-btn slot="activator" block depressed color="primary" @click="downloadAttachment()" :loading="loading" :disabled="isShipmentReadonly" class="mb-0">
                <v-icon small left>get_app</v-icon>
                {{ $t('Download all') }}
              </v-btn>
              <span>{{ $t('Download all attachments') }}</span>
            </v-tooltip>
          </v-flex>
          <v-flex xs12 sm4 :pl-2="!$vuetify.breakpoint.xs" v-if="!nonDeleteable">
            <v-tooltip bottom>
              <v-btn slot="activator" block depressed @click="deleteAttachment()" :loading="loading" :disabled="isShipmentReadonly" class="mb-0">
                <v-icon small left>delete</v-icon>
                {{ $t('Delete all') }}
              </v-btn>
              <span>{{ $t('Delete all attachments') }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

      </v-card-text></v-card>
    </v-menu>
  </div>
</template>

<script>
import Alert           from '@/components/Alert.vue';
import VInputFile      from '@/components/VInputFile.vue';
import { attachments } from "@/resources.js";
import { downloadFile, attachmentTypes, extractBodyErrorMessages } from "@/components/util.js";

export default {
  props: {
    shipment: {
      type: Object,
      default: () => {}
    },
    flag: {
      type: String,
      default: 'return_doc'
    },
    isShipmentReadonly: {
      type: Boolean
    },
    nonDeleteable: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Alert,
    VInputFile
  },
  data() {
    return {
      isMenu: false,
      isFlagChanged: false,
      loading: false,
      attachments: []
    }
  },
  computed: {
    item() {
      return this.shipment;
    },
    returnFlag() {
      return this.item[this.flag];
    },
    attachmentType() {
      return this.flag ? this.flag.substr(-3) : null;
    },
    attachmentCounted() {
      return this.attachments.length;
    },
    attachmentCount() {
      return this.attachmentCounted || this.item.attachment_count || 0;
    },
  },
  methods: {
    // Attachments
    listAttachments() {
      if (this.loading) {
        return;
      }
      this.loading = true;

      const params = {
        shipmentId:     this.item.id,
        attachmentType: this.attachmentType
      };

      attachments.list( params ).then(r => {
        if (r.body && r.body.files) {
          this.attachments = r.body.files.map(elem => ({ ...elem, type: (r.body.type || this.attachmentType.toUpperCase()) }));
        }
      }).catch(e => {
        this.$refs.msg.popup(this.$t('Error loading attachments info'), 'error');

        this.attachments = [];
        console.log('Error attachments info', e);
      }).finally(() => {
        this.loading = false;
      });
    },
    downloadAttachment(attachmentId) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      const params = {
        shipmentId:     this.item.id,
        attachmentType: this.attachmentType,
        attachmentId:   attachmentId || 'all'
      }
      // console.log('Download, params=', params);

      attachments.download( params ).then((response) => {

        downloadFile(response);
        let filename = '';
        const cd = response.headers.map['content-disposition'];
        if (cd && cd[0]) {
          const match = cd[0].match( /filename=\"?([^"]+)(?:\"?|$)/ );
          filename = match ? match[1] : '';
        }
        this.$refs.msg.popup(this.$t('ATTACHMENT.SHIPMENT.DOWNLOADED', [filename]), 'success');

      }).catch((err) => {
        console.log('Error downloading file(s)', err);

        this.$refs.msg.popup( extractBodyErrorMessages(err.body) || this.$t('Error downloading file(s)'), 'error' );

      }).finally(() => {
        this.loading = false;
      });
    },
    deleteAttachment(attachmentId) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      const params = {
        shipmentId:     this.item.id,
        attachmentType: this.attachmentType,
        attachmentId:   attachmentId || 'all'
      }
      // console.log('Delete, params=', params);

      attachments.delete( params ).then((response) => {

        setTimeout(() => {
          this.listAttachments();
        }, 500);

      }).catch((err) => {

        console.log('Error deleting file(s)', err);
        this.$refs.msg.popup( extractBodyErrorMessages(err.body) || this.$t('Error deleting file(s)'), 'error' );

      }).finally(() => {
        this.loading = false;
      });
    },
    uploadAttachment(evt) {
      if (!evt) {
        return;
      }
      if (!this.item.id) {
        this.$refs.msg.popup(this.$t('You don\'t have a shipment (saved) to attach to!'), 'warning');
        return;
      }
      const data = {
        shipmentId: this.item.id,
        type: this.attachmentType.toUpperCase()
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      formData.append('file', evt);
      // console.log('Uploading attachment, data=', data);

      attachments.upload({}, formData).then(r => {

        this.listAttachments();

      }).catch(e => {

        this.$refs.msg.popup(extractBodyErrorMessages(e.body) || this.$t('Error uploading attachment'), 'error');
        console.log('Error uploading attachment', e);

      }).finally(() => {
        this.loading = false;
      });
    },
    // Helpers
    onMenuBtn() {
      if (this.isFlagChanged) {
        this.isMenu = false;
        this.isFlagChanged = false;
      }
      else {
        this.isMenu = !this.isMenu;
      }

      if (this.isMenu) {
        this.listAttachments();
      }
    },
    getAttachmentColor(type) {
      type = type.toUpperCase();
      const attch = attachmentTypes.find(elem => elem.id === type);
      if (! attch)
        return '';
      return attch.color;
    },
    formatDatetime(timestamp, dateOnly) {
      if (!timestamp) return "";
      let d = new Date( timestamp ),
          year  = d.getFullYear(),
          month = ("0" + (d.getMonth()+1) ).slice(-2),
          day   = ("0" +  d.getDate()     ).slice(-2),
          hour  = ("0" +  d.getHours()    ).slice(-2),
          min   = ("0" +  d.getMinutes()  ).slice(-2);
      let time = `${hour}:${min}`,
          date = `${day}.${month}.${year}`;
      return dateOnly ? `${date}` : `${date} ${time}`;
    }
  },
  watch: {
    returnFlag(val) {
      if (!val) {
        this.isFlagChanged = true;
      }
    }
  }
}
</script>

<style scoped>
:deep() .v-badge--overlap .v-badge__badge {
  top: -4px;
  right: -4px;
}
</style>